import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Banner, Button, Card } from '../design/components'

interface Props {
  children: ReactNode
  /**
   * Names what failed, for the heading. A route boundary says which screen; the root one
   * has nothing more specific to offer and takes the default.
   */
  what?: string
  /**
   * Rendered under the reason — a way out of a screen that will not render. Route
   * boundaries pass a link back to somewhere that will.
   */
  escape?: ReactNode
}

interface State {
  error: Error | null
}

/**
 * A render crash, contained.
 *
 * **Route-level, not only at the root.** A boundary that only wraps the whole app turns
 * one screen's bug into a blank window with no navigation and no way back — at a table,
 * mid-session, with a phone. Wrapped per route (see `router.tsx`), a broken sheet leaves
 * the header, the tabs and every other screen working, and the failure is reported where
 * it happened.
 *
 * This is for genuine crashes only. The expected empty states — a 404, a campaign that is
 * not yours, a failed fetch — are handled by the screens themselves, because each of them
 * has something specific and useful to say and a boundary does not.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error', error, info.componentStack)
  }

  override render(): ReactNode {
    const { error } = this.state
    if (error === null) return this.props.children

    return (
      <div style={{ padding: 'var(--gutter)' }}>
        <Card variant="harm" label={this.props.what ?? 'Something broke'}>
          <Banner tone="error" urgent>
            {error.message}
          </Banner>
          <div style={{ marginTop: 'var(--sp-4)', display: 'grid', gap: 'var(--sp-2)' }}>
            {/* Clearing the error re-renders the same tree, which is worth one attempt: a
                crash on a half-loaded response usually does not repeat on the next one. */}
            <Button variant="secondary" onClick={() => this.setState({ error: null })}>
              Try again
            </Button>
            {this.props.escape}
          </div>
        </Card>
      </div>
    )
  }
}
